<template>
    <v-dialog :value="value" max-width="760px" persistent scrollable @input="close()">
        <v-card class="copy-dialog-card">
            <div class="copy-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div>
                        <div class="copy-dialog-title">
                            <v-icon class="mr-2" color="white" size="20">mdi-content-copy</v-icon>
                            Copy &amp; Customise Quote
                        </div>
                        <div class="copy-dialog-subtitle mt-1" v-if="quote">{{ quote.name }}</div>
                    </div>
                    <v-btn icon dark @click="close()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <v-card-text class="pa-6">
                <div v-if="loading" class="text-center py-10">
                    <v-progress-circular indeterminate color="#3988AA" />
                </div>
                <template v-else-if="quote">
                    <v-text-field v-model="name" label="Quote Name" outlined dense hide-details class="mb-2" />
                    <div class="copy-hint mb-5">
                        Services kept from this quote are booked once with the supplier: they won't appear twice on
                        the events list or the Suppliers tab.
                    </div>

                    <template v-if="accommodation_builds.length">
                        <div class="copy-section-title">Accommodation</div>
                        <div v-for="build in accommodation_builds" :key="build.id" class="copy-build-row">
                            <v-checkbox v-model="kept" :value="build.id" hide-details dense class="mt-0 pt-0" />
                            <div class="copy-build-name">
                                {{ buildLabel(build) }}
                                <div class="copy-build-meta">{{ nightsLabel(accommodationDates(build)) }}</div>
                            </div>
                            <template v-if="kept.includes(build.id)">
                                <v-select :items="startDateOptions(build)" :value="accommodationDates(build).start_date"
                                    @change="val => setAccommodationDate(build, 'start_date', val)"
                                    label="Check in" outlined dense hide-details class="copy-date-select mr-2" />
                                <v-select :items="endDateOptions(build)" :value="accommodationDates(build).end_date"
                                    @change="val => setAccommodationDate(build, 'end_date', val)"
                                    label="Check out" outlined dense hide-details class="copy-date-select" />
                            </template>
                        </div>
                    </template>

                    <template v-for="group in other_build_groups">
                        <div class="copy-section-title" :key="'title-' + group.label">{{ group.label }}</div>
                        <div v-for="build in group.builds" :key="build.id" class="copy-build-row">
                            <v-checkbox v-model="kept" :value="build.id" hide-details dense class="mt-0 pt-0" />
                            <div class="copy-build-name">
                                {{ buildLabel(build) }}
                                <div class="copy-build-meta">{{ $date(build.start_date).format('ddd DD/MM/YYYY') }}</div>
                            </div>
                            <div class="copy-build-rate">{{ currency }}{{ Number(build.rate || 0).toFixed(2) }}</div>
                        </div>
                    </template>

                    <div class="copy-section-title">Price</div>
                    <v-row dense>
                        <v-col cols="3">
                            <v-select v-model="discount_type" :items="adjustment_types" label="Discount Type" outlined
                                dense hide-details />
                        </v-col>
                        <v-col cols="3">
                            <v-text-field v-model="discount" label="Discount" type="number" min="0" outlined dense
                                hide-details />
                        </v-col>
                        <v-col cols="3">
                            <v-select v-model="surcharge_type" :items="adjustment_types" label="Surcharge Type" outlined
                                dense hide-details />
                        </v-col>
                        <v-col cols="3">
                            <v-text-field v-model="surcharge" label="Surcharge" type="number" min="0" outlined dense
                                hide-details />
                        </v-col>
                    </v-row>
                    <div class="copy-totals mt-4">
                        <div>Subtotal: <b>{{ currency }}{{ subtotal.toFixed(2) }}</b></div>
                        <div v-if="total !== null">Total: <b>{{ currency }}{{ total.toFixed(2) }}</b></div>
                    </div>

                    <v-checkbox v-if="event_id" v-model="add_to_event" hide-details dense class="mt-4"
                        label="Add this copy to the event's packages" />
                </template>
            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn text class="copy-btn" @click="close()">Cancel</v-btn>
                <v-btn depressed dark color="#3988AA" class="copy-btn" :loading="saving"
                    :disabled="loading || !quote || !name || kept.length === 0" @click="save()">
                    <v-icon small left>mdi-content-copy</v-icon>
                    Save Copy
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"

const TYPE_LABELS = { 2: 'Day Activity', 6: 'Night Activity', 8: 'Breakfast', 4: 'Finger Food', 5: 'Dinner', 3: 'Transportation', 7: 'Other' }

// Copy Quote with customisation, like My Stagit's "Customize Quote": pick which services to
// keep, shorten the stay, set discount/surcharge. The API links the copied services to the
// originals (see QuoteCopyService) so they're booked once with each supplier.
export default {
    props: {
        value: { type: Boolean, default: false },
        quote_id: { type: [Number, String], default: null },
        lead_id: { type: [Number, String], default: null },
        // Offer to add the copy to this event's packages (the event's Quotes tab, or a lead with an event).
        event_id: { type: [Number, String], default: null },
    },
    data: () => ({
        loading: false,
        saving: false,
        quote: null,
        name: '',
        kept: [],
        accommodation: {},
        discount_type: 'amount',
        discount: null,
        surcharge_type: 'amount',
        surcharge: null,
        add_to_event: true,
        adjustment_types: [
            { text: 'Amount', value: 'amount' },
            { text: 'Percent', value: 'percent' },
        ],
    }),
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        currency() {
            return this.get_selected_company ? this.get_selected_company.currency : ''
        },
        builds() {
            return this.quote ? (this.quote.builds || []) : []
        },
        accommodation_builds() {
            return this.builds.filter(build => build.type_id == 1)
        },
        other_build_groups() {
            return [2, 6, 8, 4, 5, 3, 7]
                .map(id => ({ label: TYPE_LABELS[id], builds: this.builds.filter(build => build.type_id == id) }))
                .filter(group => group.builds.length > 0)
        },
        subtotal() {
            return this.builds
                .filter(build => this.kept.includes(build.id))
                .reduce((sum, build) => {
                    let rate = Number(build.rate || 0)
                    if (build.type_id == 1) {
                        const dates = this.accommodationDates(build)
                        const nights = this.$date(dates.end_date).diff(this.$date(dates.start_date), 'day')
                        rate *= nights > 0 ? nights : 1
                    }
                    return sum + rate
                }, 0)
        },
        // Same as the API: the editor's profit margin isn't stored, so carry over whatever the
        // original's total had on top of its own subtotal, discount and surcharge.
        total() {
            if (!this.quote || this.quote.total === null || this.quote.total === undefined) return null
            const q = this.quote
            const margin = Number(q.total) - this.applyAdjustments(Number(q.subtotal || 0), q.discount_type, q.discount, q.surcharge_type, q.surcharge)
            const total = this.applyAdjustments(this.subtotal, this.discount_type, this.discount, this.surcharge_type, this.surcharge) + margin
            return Math.max(0, total)
        },
    },
    watch: {
        value(open) {
            if (open) this.load()
        },
    },
    methods: {
        async load() {
            this.loading = true
            this.quote = null
            try {
                const response = await this.$axios.get(`admin/quotes/get_quote/${this.quote_id}`)
                if (response.data.response) {
                    const quote = response.data.data
                    this.quote = quote
                    this.name = `${quote.name} (copy)`
                    this.kept = (quote.builds || []).map(build => build.id)
                    this.accommodation = {}
                    this.discount_type = quote.discount_type || 'amount'
                    this.discount = quote.discount
                    this.surcharge_type = quote.surcharge_type || 'amount'
                    this.surcharge = quote.surcharge
                    this.add_to_event = true
                } else {
                    this.$toast.error(response.data.message || 'Failed to load quote')
                }
            } catch (error) {
                this.$toast.error('Failed to load quote')
            }
            this.loading = false
        },
        buildLabel(build) {
            const supplier = build.supplier ? build.supplier.supplier_name : 'Supplier'
            return build.option ? `${supplier} - ${build.option.name}` : supplier
        },
        day(date) {
            return this.$date(date).format('YYYY-MM-DD')
        },
        accommodationDates(build) {
            return this.accommodation[build.id] || { start_date: this.day(build.start_date), end_date: this.day(build.end_date) }
        },
        nightsLabel(dates) {
            const nights = this.$date(dates.end_date).diff(this.$date(dates.start_date), 'day')
            return `${this.$date(dates.start_date).format('ddd DD/MM')} - ${this.$date(dates.end_date).format('ddd DD/MM')} (${nights} night${nights === 1 ? '' : 's'})`
        },
        // Any run of the original stay's nights.
        stayDays(build) {
            const start = this.$date(build.start_date)
            const nights = Math.max(this.$date(build.end_date).diff(start, 'day'), 1)
            return Array.from({ length: nights + 1 }, (unused, i) => start.add(i, 'day'))
        },
        startDateOptions(build) {
            return this.stayDays(build).slice(0, -1).map(date => ({ text: date.format('ddd DD/MM/YYYY'), value: date.format('YYYY-MM-DD') }))
        },
        endDateOptions(build) {
            const start = this.accommodationDates(build).start_date
            return this.stayDays(build)
                .filter(date => date.format('YYYY-MM-DD') > start)
                .map(date => ({ text: date.format('ddd DD/MM/YYYY'), value: date.format('YYYY-MM-DD') }))
        },
        setAccommodationDate(build, field, value) {
            const dates = { ...this.accommodationDates(build), [field]: value }
            if (dates.end_date <= dates.start_date) {
                dates.end_date = this.$date(dates.start_date).add(1, 'day').format('YYYY-MM-DD')
            }
            this.$set(this.accommodation, build.id, dates)
        },
        applyAdjustments(subtotal, discount_type, discount, surcharge_type, surcharge) {
            const discount_value = Number(discount) || 0
            const surcharge_value = Number(surcharge) || 0
            return subtotal
                - (discount_type === 'percent' ? (discount_value / 100) * subtotal : discount_value)
                + (surcharge_type === 'percent' ? (surcharge_value / 100) * subtotal : surcharge_value)
        },
        async save() {
            this.saving = true
            const accommodation = {}
            this.accommodation_builds
                .filter(build => this.kept.includes(build.id) && this.accommodation[build.id])
                .forEach(build => { accommodation[build.id] = this.accommodation[build.id] })

            const payload = {
                id: this.quote.id,
                lead_id: this.lead_id || this.quote.lead_id,
                company_id: this.get_selected_company.id,
                copy: {
                    name: this.name,
                    build_ids: this.kept,
                    accommodation,
                    event_id: this.event_id && this.add_to_event ? this.event_id : null,
                    discount_type: this.discount_type,
                    discount: this.discount === '' ? null : this.discount,
                    surcharge_type: this.surcharge_type,
                    surcharge: this.surcharge === '' ? null : this.surcharge,
                },
            }
            try {
                const response = await this.$axios.post('admin/quotes/copy_quote', payload)
                const result = response.data
                if (result.response == true) {
                    this.$toast.success(result.message, { timeout: 2000, icon: true })
                    this.$emit('copied', result)
                    this.close()
                } else {
                    this.$toast.error(result.message)
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to copy quote')
            }
            this.saving = false
        },
        close() {
            this.$emit('input', false)
        },
    },
}
</script>

<style lang="scss" scoped>
.copy-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
    font-family: Manrope;
}

.copy-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.copy-dialog-title {
    font-size: 18px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.copy-dialog-subtitle {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
}

.copy-hint {
    font-size: 12px;
    color: #7C7C7C;
}

.copy-section-title {
    font-size: 11px;
    font-weight: 700;
    color: #9E9E9E;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 18px 0 6px;
}

.copy-build-row {
    display: flex;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid #F0F0F0;
}

.copy-build-name {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: #3D3D3D;
}

.copy-build-meta {
    font-size: 12px;
    font-weight: 500;
    color: #7C7C7C;
}

.copy-build-rate {
    font-size: 13px;
    color: #7C7C7C;
}

.copy-date-select {
    max-width: 170px;
}

.copy-totals {
    text-align: right;
    font-size: 14px;
    color: #3D3D3D;
}

.copy-btn {
    font-weight: 600 !important;
    font-size: 13px !important;
    text-transform: none;
}
</style>
